import api, { ApiResponse } from './api';

export interface Schedule {
  id: number;
  dazn_asset_id: string;
  event_title: string;
  event_description: string | null;
  event_date: string;
  event_start_time: string;
  event_end_time: string | null;
  home_team: string | null;
  away_team: string | null;
  status: 0 | 1 | 2 | 3 | 4; // 0=scheduled, 1=live, 2=completed, 3=error, 4=vod
  status_message: string | null;
  xaccel_channel_name: string | null;
  xaccel_stream_url: string | null;
  started_at: string | null;
  stopped_at: string | null;
  competition_id?: number;
  competition_name?: string;
  channel_mode?: 'numbered' | 'team' | 'both';
  sport_name?: string;
  xaccel_real_status?: string;
  server_type?: string;
  servers?: Array<{ id: number; name: string; server_type: string; status: string }>;
  /** Status 0 after a partial multi-resolution start (some legs live, the retry starts the rest); absent otherwise */
  partial_start?: { running: number; total: number };
  /** Badge "a pagamento": 'ppv' = solo pay-per-view, 'extra' = richiede pacchetto/addon, 'none' = standard */
  paywall?: 'none' | 'extra' | 'ppv';
  paywall_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface ScheduleByDateResponse {
  schedules: Schedule[];
  grouped: {
    scheduled: Schedule[];
    live: Schedule[];
    completed: Schedule[];
    error: Schedule[];
  };
}

export const scheduleService = {
  async list(params?: { date?: string; status?: number }): Promise<Schedule[]> {
    const response = await api.get<ApiResponse<Schedule[]>>('/schedule', { params });
    return response.data.data;
  },

  async getByDate(date: string): Promise<ScheduleByDateResponse> {
    const response = await api.get<ApiResponse<ScheduleByDateResponse>>(`/schedule/date/${date}`);
    return response.data.data;
  },

  async generateFromEpg(date: string, competitionIds?: number[]): Promise<{ data: Schedule[]; message: string }> {
    const response = await api.post<ApiResponse<Schedule[]> & { message: string }>(
      `/schedule/generate/${date}`,
      competitionIds ? { competition_ids: competitionIds } : undefined
    );
    return { data: response.data.data, message: response.data.message || '' };
  },

  async clearByDate(date: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<null> & { message: string }>(`/schedule/clear/${date}`);
    return { message: response.data.message || '' };
  },

  async startEvent(assetId: string): Promise<Schedule> {
    const response = await api.post<ApiResponse<Schedule>>(`/schedule/event/${assetId}/start`);
    return response.data.data;
  },

  async stopEvent(assetId: string): Promise<Schedule> {
    const response = await api.post<ApiResponse<Schedule>>(`/schedule/event/${assetId}/stop`);
    return response.data.data;
  },

  async resetEvent(assetId: string): Promise<Schedule> {
    const response = await api.post<ApiResponse<Schedule>>(`/schedule/event/${assetId}/reset`);
    return response.data.data;
  },

  async downloadSchedule(date: string): Promise<Blob> {
    const response = await api.get(`/schedule/download/${date}`, { responseType: 'blob' });
    return response.data;
  },
};
